import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { SolutieRand } from "../nav/api";
import type { CanonicalDoc, StoreyPlan } from "./canonical";
import { cheieDeCadru } from "./cadru";
import type { Selection } from "./inspect";
import type { ApartmentLevel } from "./plan/ApartmentView";
import { fitBbox, padBbox, ringBbox, rotateView, scrollIntent, K_MAX,
         type Bbox, type View } from "./view";

/** Cât de mult se poate depărta desenul, și pentru încadrare, și pentru rotiță.
 *
 *  UNA singură, fiindcă `fitBbox` are un implicit al lui (`K_MIN`, 4 px/m) iar
 *  rotița avea altul (2), scris de mână. Nepotrivirea nu era teoretică: un lot
 *  de 322 m cere vreo 1,5 px/m ca să încapă, `fitBbox` îl ridica la 4, iar
 *  desenul se deschidea atârnând peste ambele margini. Și fiindcă ambele
 *  variante — dreaptă și întoarsă — se opreau în același prag, raportul dintre
 *  ele ieșea mereu 1, deci întoarcerea automată nu se declanșa niciodată. */
const K_JOS = 0.15;

/** Cât stă zoom-ul pe loc până se redesenează detaliul la scara nouă. Bucla de
 *  cadru mai alunecă ~250 ms după ultimul tic de rotiță; pragul curge de acolo. */
const ASEZARE_MS = 120;

/** SCARA DETALIULUI: `view.k`, dar numai după ce zoom-ul s-a așezat.
 *
 *  Straturile planului primesc scara ca să-și aleagă detaliul (hașura,
 *  mobilierul, numele camerelor, cotele cu corp fix pe ecran). Cu scara vie
 *  ele se refăceau la FIECARE cadru de zoom: pe documentul Demo-UTR6 (4 corpuri,
 *  ~7 000 de noduri SVG) etajul costa ~58 ms și numele cu cotele 25–75 ms pe
 *  cadru, în modul de dezvoltare, adică 8–10 cadre pe secundă în loc de 60.
 *  Cât ține gestul, desenul se mărește prin transformarea grupului, care e
 *  ieftină; detaliul se reface o dată, la oprire. */
export function useScaraDetaliului(k: number | null): number | null {
  const [asezat, setAsezat] = useState(k);
  useEffect(() => {
    if (k === asezat) return;
    // Prima scară (desenul abia încadrat) intră pe loc, nu după pauză.
    if (asezat == null || k == null) { setAsezat(k); return; }
    const id = setTimeout(() => setAsezat(k), ASEZARE_MS);
    return () => clearTimeout(id);
  }, [k, asezat]);
  return asezat ?? k;
}

/** Planșeul pe care se citește selecția — v. `etajActiv` în `Vizualizator`
 *  (tipul cu care îl vede TypeScript acolo). */
type EtajActiv = ApartmentLevel;

/** ZOOM, PANORAMARE ȘI ROTIRE pe desenul 2D: încadrarea, camera moștenită
 *  între documente și bucla de cadru amortizată. Comenzile (tastele, rotița,
 *  gesturile) se leagă separat, în `useComenziPanZoom`, la locul lor din
 *  componentă.
 *
 *  `view` și `rot` rămân declarate în `Vizualizator`, unde erau (între ele și
 *  restul stă starea legendei): ordinea hook-urilor nu se schimbă.
 *
 *  Nu stă peste `comenzi3d.ts`: acolo tastele merg la vederea atinsă ultima
 *  (aici la fereastră, iar Escape lasă și rigla), pasul se aplică direct (aici
 *  bucla amortizează, cu alți pași de zoom), rotița nu ocolește panourile
 *  plutitoare, iar modulul aduce three.js în bundle-ul vizualizatorului. */
export function usePanZoom({
  view, setView, rot, setRot, doc, origine, cadru, compact, selectie,
  etajActiv, tintaRezolvata, inelLocal, arhActiv, solution, corpActiv,
  vedeSituatia, machetaSit, cheieTintaStabila,
}: {
  view: View | null;
  setView: Dispatch<SetStateAction<View | null>>;
  rot: number;
  setRot: Dispatch<SetStateAction<number>>;
  doc: CanonicalDoc | null;
  origine: [number, number];
  cadru: { bbox: Bbox } | null;
  compact: boolean;
  selectie: Selection | null;
  etajActiv: EtajActiv;
  tintaRezolvata: { etaj: EtajActiv; sel: Selection } | null;
  inelLocal: (sel: Selection | null | undefined, etaj: EtajActiv) => number[][] | null;
  arhActiv: StoreyPlan | null;
  solution: SolutieRand;
  corpActiv: string;
  vedeSituatia: boolean;
  machetaSit: boolean;
  cheieTintaStabila: string | null;
}) {
  /** Pivotul mutat de om cu dublu-clic. `null` = centrul cadrului. */
  const [pivotManual, setPivotManual] = useState<{ x: number; y: number } | null>(null);
  const [dim, setDim] = useState<{ w: number; h: number } | null>(null);
  const gazda = useRef<HTMLDivElement>(null);
  const viewRef = useRef<View | null>(null);
  const tragere = useRef<{ sx: number; sy: number; tx: number; ty: number;
                           target: EventTarget | null } | null>(null);
  viewRef.current = view;
  // Citite de ascultătorul de gesturi, care se atașează O DATĂ: fără ele ar
  // trebui re-atașat la fiecare rotire sau redimensionare.
  const rotRef = useRef(0); rotRef.current = rot;
  const dimRef = useRef<{ w: number; h: number } | null>(null);
  const pivotRef = useRef<{ x: number; y: number } | null>(null);
  /** Originea locală a documentului AFIȘAT — `null` cât nu e niciunul. */
  const origineRef = useRef<[number, number] | null>(null);
  origineRef.current = doc ? origine : null;
  /** Camera documentului de dinainte, de tradus în cadrul celui nou. */
  const mostenire = useRef<{ view: View; rot: number; origine: [number, number];
                             pivotVechi: { x: number; y: number } } | null>(null);
  /** Chemată de efectul de deschidere a unei soluții (v. `useDocumentSolutie`),
   *  în locul în care stătea acolo. */
  const mostenesteCamera = () => {
    // CAMERA SE MOȘTENEȘTE de la documentul de dinainte. Trecerea de la planul
    // de situație la arhitectură (și înapoi) e aceeași parcelă în două desene,
    // iar omul care se uita la colțul de nord-est al lotului vrea să vadă
    // ACELAȘI colț, la același zoom — nu tot desenul reîncadrat. Se rețin
    // zoom-ul, panoramarea, rotirea și originea locală a desenului vechi;
    // `incadreaza` le traduce în cadrul noului document când acesta sosește.
    if (viewRef.current && origineRef.current) {
      mostenire.current = { view: viewRef.current, rot: rotRef.current,
                            origine: origineRef.current,
                            pivotVechi: pivotRef.current ?? { x: 0, y: 0 } };
    }
    setPivotManual(null);
  };

  // Dimensiunea zonei de desen, urmărită: se schimbă la redimensionarea
  // ferestrei și la deschiderea inspectorului.
  useEffect(() => {
    const el = gazda.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setDim({ w: Math.round(r.width), h: Math.round(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /** Cadrul pe care se încadrează.
   *
   *  Fără sfert de tură propriu: cadrul se măsoară pe geometria din `cadru`,
   *  iar ea e deja în cadrul desenului. Cât timp orientarea se aplica doar
   *  stratului de situație, aici trebuia repetată de mână — și, repetată de
   *  mână, trebuia și ținută minte. */
  const bboxDesen = useMemo<Bbox | null>(() => cadru?.bbox ?? null, [cadru]);

  const pivot = useMemo(() => {
    if (pivotManual) return pivotManual;
    if (!bboxDesen) return null;
    const [x0, y0, x1, y1] = bboxDesen;
    return { x: (x0 + x1) / 2, y: (y0 + y1) / 2 };
  }, [bboxDesen, pivotManual]);

  /** Încadrarea.
   *
   *  Plafonul implicit de mărire (26 px/m) e croit pentru un lot: o parcelă
   *  mărită peste el umple ecranul cu asfalt. O clădire de 21 × 18 m e alt
   *  ordin de mărime — la 26 px/m rămânea un desen cât o carte poștală.
   *
   *  NORDUL ÎN SUS LA DESCHIDERE, oricât ar risipi.
   *
   *  Aici se întorcea desenul un sfert de tură când așa umplea fereastra mai
   *  bine — un lot de 321 pe 102 m pus în picioare într-o fereastră lată se
   *  desenează cât o panglică, cu jumătate de ecran gol de-o parte și de alta.
   *  Câștigul e real, dar plătit în ce nu se vede: planșa se deschidea deja
   *  întoarsă, iar cine se uita la ea citea o orientare care nu e a terenului.
   *  Pe o planșă de situație asta nu e o preferință de încadrare, e o citire
   *  greșită — nordul e o proprietate a locului, nu a ferestrei.
   *
   *  Cine vrea altă orientare o cere: `q`/`e` rotesc, iar busola arată oricând
   *  cât s-a întors. Ce se rotește rămâne rotit — `mostenire` duce unghiul mai
   *  departe între planșe, deci alegerea omului nu se pierde la fiecare nivel
   *  schimbat. Ce nu se mai întâmplă e ca desenul să se întoarcă singur, fără
   *  să-i fi cerut nimeni. */
  const incadreaza = useCallback((auto = false) => {
    if (!bboxDesen || !dim || dim.w < 50 || !pivot) return;
    // CAMERA MOȘTENITĂ de la documentul de dinainte (v. efectul de schimbare a
    // soluției). Cele două documente stau în aceleași coordonate geografice,
    // dar fiecare e adus la ORIGINEA lui locală, iar rotirea se face în jurul
    // pivotului LUI. Transformarea veche, `translate(tx) scale(k) rotate(rot,
    // p)`, se rescrie deci cu pivotul nou și cu punctele deplasate cu
    // diferența de origine, astfel încât orice punct de pe teren să cadă pe
    // același pixel ca înainte. Numai pe calea automată: «nord» înseamnă
    // «arată-mi tot», și acolo moștenirea se aruncă.
    const m = mostenire.current;
    if (m) mostenire.current = null;
    if (auto && m) {
      const k = m.view.k;
      const dx = origine[0] - m.origine[0], dy = origine[1] - m.origine[1];
      const th = (m.rot * Math.PI) / 180;
      const c = Math.cos(th), sn = Math.sin(th);
      // q = pivotul nou − pivotul vechi + deplasarea originii, în cadrul vechi
      const qx = pivot.x - m.pivotVechi.x + dx, qy = pivot.y - m.pivotVechi.y + dy;
      const tx = m.view.tx + k * ((c * qx - sn * qy) - (pivot.x - m.pivotVechi.x));
      const ty = m.view.ty + k * ((sn * qx + c * qy) - (pivot.y - m.pivotVechi.y));
      setRot(m.rot);
      spre.current.k = k; spre.current.rot = m.rot; spre.current.ancora = null;
      setView({ k, tx, ty });
      return;
    }
    // În previzualizare se încadrează ELEMENTUL, nu etajul. Un etaj întreg
    // într-o casetă de 240 px face din camera judecată câțiva pixeli — adică
    // exact lucrul pentru care există caseta nu se vede. `padM` mai mare decât
    // la etaj: un contur lipit de margine nu se citește ca fiind într-un plan.
    //
    // Nici întoarcere, nici rotire păstrată: caseta arată UN element, iar dacă
    // s-ar deschide strâmb nu s-ar înțelege de ce.
    if (compact && selectie) {
      const inel = inelLocal(selectie, etajActiv);
      const b = inel && ringBbox(inel);
      if (b) {
        const v = fitBbox(padBbox(b, 3), dim.w, dim.h, { padM: 0, minK: K_JOS });
        setRot(0);
        spre.current.k = v.k; spre.current.rot = 0; spre.current.ancora = null;
        setView(v);
        return;
      }
    }
    // ZOOM LA SOSIRE: o țintă venită din validator mută cadrul pe ea.
    //
    // Reîncadrarea refuză deliberat să urmeze SELECȚIA (v. efectul de mai jos),
    // și bine face: altfel fiecare click ar arunca zoom-ul și panoramarea
    // omului. Dar o țintă nu e un click — e o cerere explicită de a fi dus
    // undeva («Vezi pe planșă» din ecranul de verificări). Fără mutare, linkul
    // deschidea planul întreg, cu tronsonul judecat de câțiva pixeli, undeva.
    //
    // Numai pe calea AUTOMATĂ (`auto`): `laNord` cheamă `incadreaza(false)`, și
    // butonul ăla înseamnă «arată-mi tot», nu «du-mă înapoi la țintă».
    //
    // Padding generos, spre deosebire de casetă: acolo se arată UN element, aici
    // omul trebuie să vadă și în ce apartament e, altfel nu știe unde a ajuns.
    if (auto && !compact && tintaRezolvata) {
      const inel = inelLocal(tintaRezolvata.sel, tintaRezolvata.etaj);
      const b = inel && ringBbox(inel);
      if (b) {
        const v = fitBbox(padBbox(b, 8), dim.w, dim.h,
                          { padM: 0, maxK: arhActiv ? K_MAX : 26, minK: K_JOS });
        setRot(0);
        spre.current.k = v.k; spre.current.rot = 0; spre.current.ancora = null;
        setView(v);
        return;
      }
    }
    const maxK = arhActiv ? K_MAX : 26;
    const { k } = fitBbox(bboxDesen, dim.w, dim.h, { padM: 4, maxK, minK: K_JOS });
    setRot(0);
    spre.current.k = k;
    spre.current.rot = 0;
    spre.current.ancora = null;
    // Rotirea e în jurul pivotului, iar pivotul e centrul cadrului, deci el
    // ajunge în mijlocul ferestrei oricât s-ar fi întors.
    setView({ k, tx: dim.w / 2 - k * pivot.x, ty: dim.h / 2 - k * pivot.y });
  }, [bboxDesen, dim, arhActiv, pivot, compact, selectie, etajActiv,
      tintaRezolvata, inelLocal, origine]);

  // REÎNCADRAREA URMEAZĂ CE SE DESENEAZĂ, NU CE E SELECTAT.
  //
  // `incadreaza` citește `selectie`, fiindcă în previzualizarea compactă cadrul
  // se strânge pe elementul ales — deci o are în dependențe. Efectul de aici se
  // declanșa însă pe IDENTITATEA callback-ului, așa că fiecare click îl re-crea
  // și reîncadra TOT desenul: `setView` + `setRot`, adică zoom-ul, panoramarea
  // și rotirea omului aruncate instantaneu, la fiecare selecție. De aici venea
  // „mă dă zoom out când dau click".
  //
  // În vizualizatorul normal selecția nu schimbă nimic din ce se desenează, deci
  // n-are ce reîncadra; în caseta compactă schimbă, și acolo rămâne declanșator.
  // Lista e scrisă explicit tocmai ca diferența asta să fie vizibilă.
  const dupaSelectie = compact ? selectie : null;
  // Ținta intră în listă prin CHEIA ei, nu prin obiect: altfel un al doilea
  // click pe altă regulă, cu aceeași pânză deja încărcată, n-ar reîncadra —
  // exact felul de link care „nu face nimic". Cheia se schimbă o dată per
  // țintă, deci mutarea se întâmplă o dată; după ea omul stăpânește cadrul.
  // NICI ETAJUL NU REÎNCADREAZĂ — același motiv ca la selecție, o treaptă mai
  // sus.
  //
  // Etajele unei clădiri sunt STIVUITE: parterul și etajul 7 stau în același
  // loc din lume, în același cadru. Schimbând nivelul nu se mută nimic, se
  // schimbă doar ce e desenat — deci nu e nimic de reîncadrat, iar omul care
  // mărise pe un apartament ca să-l compare cu cel de deasupra îl pierdea exact
  // când voia să-l compare. Asta era „de fiecare dată când apăs pe un etaj îmi
  // dă default view".
  //
  // Se reîncadrează când se schimbă CE se încadrează, nu ce se vede din el:
  // altă soluție, alt corp, altă planșă, altă țintă, altă fereastră. Cheia le
  // scrie pe toate explicit, ca diferența să fie vizibilă; `etajActiv` rămâne
  // în dependențe fiindcă efectul TOT trebuie să ruleze la prima sosire, când
  // `bboxDesen` abia atunci apare — dar atunci cheia e nouă oricum.
  //
  // Cheia se calculează în `cadru.ts`, ca funcție pură cu parametri numiți:
  // prima încercare a pus în ea `arhActiv?.id`, adică idul PLANȘEULUI ACTIV,
  // care se schimbă cu etajul — deci n-a reparat nimic. Acolo greșeala are un
  // test, aici n-avea.
  const cheieCadru = cheieDeCadru({
    solutie: solution.id, corp: corpActiv, situatie: vedeSituatia,
    machetaSit, areArh: !!arhActiv, compact,
    // Selecția ca ȘIR, nu ca obiect: e un obiect nou la fiecare randare, iar
    // cheia s-ar schimba de la sine. În vizualizatorul normal e oricum gol.
    selectie: dupaSelectie ? JSON.stringify(dupaSelectie) : "",
    tinta: cheieTintaStabila ?? "", dim,
  });
  const cadrulFacut = useRef<string | null>(null);
  useEffect(() => {
    if (cadrulFacut.current === cheieCadru) return;
    // Nu se marchează drept făcut până CHIAR se poate face: `incadreaza` iese
    // singur pe geometrie lipsă, iar marcat înainte, cadrul n-ar mai veni
    // niciodată când geometria sosește.
    if (!bboxDesen || !dim || dim.w < 50 || !pivot) return;
    cadrulFacut.current = cheieCadru;
    incadreaza(true);
  },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [bboxDesen, dim, arhActiv, pivot, compact, dupaSelectie, etajActiv,
             cheieTintaStabila, cheieCadru]);

  dimRef.current = dim;
  pivotRef.current = pivot;

  /** Unde ajunge desenul, nu unde e acum.
   *
   *  Zoom-ul și rotirea se scriu AICI, iar bucla de cadru le urmărește cu
   *  amortizare. Aplicate direct, o rotiță de trackpad — care trimite douăzeci
   *  de evenimente pe secundă — face desenul să zvâcnească; urmărite, alunecă. */
  const spre = useRef({ k: 0, rot: 0, ancora: null as { x: number; y: number } | null });
  /** Ce s-a adunat de la ultimul cadru. Trackpad-ul trimite mai multe evenimente
   *  între două cadre, iar aplicate pe rând sar; adunate, nu. */
  const stranse = useRef({ panX: 0, panY: 0, zoom: 0, zx: 0, zy: 0 });
  const taste = useRef(new Set<string>());
  const cadruId = useRef(0);
  const ultimulCadru = useRef(0);
  /** Cât ține un gest WebKit, evenimentele `wheel` se ignoră. */
  const gestActiv = useRef(false);

  /** Un cadru de mișcare: tastele apăsate, ce s-a strâns din scroll, apoi
   *  amortizarea spre țintă. Se oprește singură când nu mai are ce face, ca să
   *  nu redeseneze planul de sub deget la 60 de cadri pe secundă degeaba. */
  const porneste = useCallback(() => {
    if (cadruId.current) return;
    const pas = () => {
      cadruId.current = 0;
      const v = viewRef.current, d = dimRef.current, p = pivotRef.current;
      if (!v || !d || !p) return;
      let { k, tx, ty } = v;
      let r = rotRef.current;
      const T = taste.current, A = stranse.current, Z = spre.current;
      let viu = false;

      // --- taste ținute: pan cu WASD/săgeți, rotire cu Q/E, zoom cu R/F --- //
      //
      // SENSUL: se mută CAMERA, nu desenul. W urcă privirea, S o coboară, A o
      // duce la stânga, D la dreapta — ca într-un joc.
      //
      // Aici se mută `tx/ty`, adică TRANSLAȚIA CONȚINUTULUI, deci semnele sunt
      // INVERSE față de mișcarea camerei: ca privirea să urce, desenul trebuie
      // să coboare sub ea. De asta W adaugă la `ty`.
      //
      // Cine „uniformizează" semnele cu cele din `Randare3D` întoarce comanda pe
      // dos: acolo se mută camera direct, aici conținutul. V. `taste.test.ts`.
      const PAN = 9, ROT = 1.1, ZOOM = 1.012;
      let dx = 0, dy = 0;
      if (T.has("a")) dx += PAN;      // cameră stânga  ⇒ conținut la dreapta
      if (T.has("d")) dx -= PAN;      // cameră dreapta ⇒ conținut la stânga
      if (T.has("w")) dy += PAN;      // cameră sus     ⇒ conținut în jos
      if (T.has("s")) dy -= PAN;      // cameră jos     ⇒ conținut în sus
      if (dx || dy) { tx += dx; ty += dy; viu = true; }
      if (T.has("q")) { Z.rot -= ROT; viu = true; }
      if (T.has("e")) { Z.rot += ROT; viu = true; }
      if (T.has("r")) { Z.k = Math.min(60, Z.k * ZOOM); Z.ancora = null; viu = true; }
      if (T.has("f")) { Z.k = Math.max(K_JOS, Z.k / ZOOM); Z.ancora = null; viu = true; }

      // --- ce s-a strâns din scroll ---------------------------------------- //
      if (A.panX || A.panY) {
        tx += A.panX; ty += A.panY; A.panX = 0; A.panY = 0; viu = true;
      }
      if (A.zoom) {
        Z.k = Math.min(60, Math.max(K_JOS, Z.k * Math.exp(A.zoom)));
        Z.ancora = { x: A.zx, y: A.zy };
        A.zoom = 0; viu = true;
      }

      // --- amortizare spre țintă ------------------------------------------- //
      //
      // Schema cere „~0,08". Luat ca fracție pe cadru, 0,08 înseamnă 47 de
      // cadre — 780 ms — până se așază la 2% de țintă: desenul alunecă mult
      // după ce ai ridicat degetul, și se simte ca întârziere, nu ca finețe.
      // Se ține ideea (urmărire lină, nu salt) dar cu o constantă de TIMP, deci
      // și independentă de cât de des vin cadrele: la 60 Hz iese ~0,22 pe cadru
      // și se așază în ~250 ms.
      const acum = performance.now();
      const dt = Math.min(64, acum - ultimulCadru.current || 16.7);
      ultimulCadru.current = acum;
      const D = 1 - Math.exp(-dt / 80);
      if (Math.abs(Z.k - k) > k * 1e-3) {
        // Zoom-ul repivotează pe ce e sub cursor: punctul de acolo rămâne acolo,
        // deci orbita nu fuge de sub deget când te apropii.
        const k2 = k + (Z.k - k) * D;
        const a = Z.ancora ?? { x: d.w / 2, y: d.h / 2 };
        tx = a.x - (a.x - tx) * (k2 / k);
        ty = a.y - (a.y - ty) * (k2 / k);
        k = k2; viu = true;
      } else if (k !== Z.k) {
        const a = Z.ancora ?? { x: d.w / 2, y: d.h / 2 };
        tx = a.x - (a.x - tx) * (Z.k / k);
        ty = a.y - (a.y - ty) * (Z.k / k);
        k = Z.k;
      }
      if (Math.abs(Z.rot - r) > 0.02) {
        const dr = (Z.rot - r) * D;
        const nv = rotateView({ k, tx, ty }, r, dr, p, d.w, d.h);
        tx = nv.tx; ty = nv.ty; r += dr; viu = true;
      } else if (r !== Z.rot) {
        const nv = rotateView({ k, tx, ty }, r, Z.rot - r, p, d.w, d.h);
        tx = nv.tx; ty = nv.ty; r = Z.rot;
      }

      setView({ k, tx, ty });
      setRot(r);
      if (viu || T.size) cadruId.current = requestAnimationFrame(pas);
    };
    ultimulCadru.current = performance.now();
    cadruId.current = requestAnimationFrame(pas);
  }, []);

  useEffect(() => () => { if (cadruId.current) cancelAnimationFrame(cadruId.current); }, []);

  const laNord = useCallback(() => { setPivotManual(null); incadreaza(false); }, [incadreaza]);

  return { dim, gazda, viewRef, tragere, rotRef, dimRef, pivotRef, pivot,
           setPivotManual, mostenesteCamera, spre, stranse, taste, gestActiv,
           porneste, laNord };
}

/** TASTELE, ROTIȚA ȘI GESTURILE desenului 2D, legate pe fereastră și pe
 *  zona de desen. Se cheamă din `Vizualizator` în locul în care stăteau
 *  efectele, după riglă (Escape o lasă din mână). */
export function useComenziPanZoom({ panZoom, setView, setRot, stingeRigla }: {
  panZoom: ReturnType<typeof usePanZoom>;
  setView: Dispatch<SetStateAction<View | null>>;
  setRot: Dispatch<SetStateAction<number>>;
  stingeRigla: () => void;
}) {
  const { gazda, taste, porneste, laNord, stranse, gestActiv, rotRef, viewRef,
          dimRef, pivotRef, spre } = panZoom;
  // --- tastele. Mereu active pe canvas, ținute apăsat, ca într-un joc ----- //
  useEffect(() => {
    const SEMN: Record<string, string> = {
      arrowleft: "a", arrowright: "d", arrowup: "w", arrowdown: "s",
    };
    const scrie = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      return !!el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable);
    };
    const jos = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || scrie(e.target)) return;
      const b = e.key.toLowerCase();
      const g = SEMN[b] ?? b;
      if ("wasdqerf".includes(g) && g.length === 1) {
        e.preventDefault(); taste.current.add(g); porneste();
      } else if (b === "0") { e.preventDefault(); laNord(); }
      // Escape lasă rigla din mână — gestul pe care îl încearcă oricine
      // înainte să caute butonul.
      else if (b === "escape") stingeRigla();
    };
    const sus = (e: KeyboardEvent) => {
      const b = e.key.toLowerCase();
      taste.current.delete(SEMN[b] ?? b);
    };
    // Fereastra care pierde focusul nu mai trimite `keyup`, iar tasta ar rămâne
    // „apăsată" la nesfârșit.
    const goleste = () => taste.current.clear();
    window.addEventListener("keydown", jos);
    window.addEventListener("keyup", sus);
    window.addEventListener("blur", goleste);
    return () => {
      window.removeEventListener("keydown", jos);
      window.removeEventListener("keyup", sus);
      window.removeEventListener("blur", goleste);
    };
  }, [porneste, laNord, stingeRigla]);

  // --- scroll: pan cu două degete, zoom la pinch și la rotiță ------------- //
  useEffect(() => {
    const el = gazda.current;
    if (!el) return;
    const laRotita = (e: WheelEvent) => {
      // ROTIȚA PESTE UN PANOU CARE DERULEAZĂ e a panoului: lista de reguli a
      // fațadei, discuția asistentului, inspectorul plutitor (cu variantele de
      // recompartimentare e mai înalt decât ecranul), legenda. Altfel
      // `preventDefault` de mai jos oprea derularea lor și rotița apropia
      // macheta de sub ele.
      if ((e.target as HTMLElement | null)?.closest?.(
            ".panou-fatada, .ob-as, .inspector-plutitor, .legenda-plutitoare")) return;
      // Fără asta browserul dă zoom pe pagină la pinch și pleacă înapoi în
      // istoric la scroll orizontal.
      e.preventDefault();
      if (gestActiv.current) return;
      const r = el.getBoundingClientRect();
      const A = stranse.current;
      // Pinch mărește, scroll mută — v. `scrollIntent`, unde stă și de ce nu mai
      // e nimic de ghicit.
      if (scrollIntent(e) === "zoom") {
        A.zoom += -e.deltaY * 0.01;
        A.zx = e.clientX - r.left; A.zy = e.clientY - r.top;
      } else {
        A.panX -= e.deltaX; A.panY -= e.deltaY;
      }
      porneste();
    };
    el.addEventListener("wheel", laRotita, { passive: false });
    return () => el.removeEventListener("wheel", laRotita);
  }, [porneste]);

  // Cele două degete pe trackpad: RĂSUCIRE și PINCH, amândouă de aici.
  //
  // Safari nu trimite pinch-ul ca `wheel` cu `ctrlKey`, cum fac Chrome și
  // Firefox — trimite gesturile lui, `gesturestart`/`gesturechange`, cu
  // `rotation` în grade și `scale` ca factor față de începutul gestului. De-aia
  // pinch-ul nu făcea nimic în Safari: regula cu `ctrlKey` nu se declanșa
  // niciodată, iar ascultătorul de aici citea doar `rotation` și arunca `scale`.
  //
  // Cât ține un gest, `wheel` se ignoră: pe un browser care le-ar trimite pe
  // amândouă, mărirea s-ar aplica de două ori.
  useEffect(() => {
    const el = gazda.current;
    if (!el) return;
    let a0 = 0, s0 = 1, rot0 = 0, v0: View | null = null;
    let anc = { x: 0, y: 0 };
    const G = (e: Event) => e as Event & {
      rotation: number; scale: number; clientX: number; clientY: number };
    const start = (e: Event) => {
      e.preventDefault();
      const g = G(e), r = el.getBoundingClientRect();
      a0 = g.rotation; s0 = g.scale || 1;
      rot0 = rotRef.current;
      v0 = viewRef.current;
      anc = { x: g.clientX - r.left, y: g.clientY - r.top };
      gestActiv.current = true;
    };
    const change = (e: Event) => {
      e.preventDefault();
      const g = G(e), d = dimRef.current, p = pivotRef.current;
      if (!v0 || !d || !p) return;
      // Manipulare directă, deci se aplică pe loc, nu prin amortizare — dar
      // ținta merge cu ea, altfel bucla ar trage înapoi de unde a plecat.
      const k2 = Math.min(60, Math.max(K_JOS, v0.k * ((g.scale || 1) / s0)));
      const marit: View = {
        k: k2,
        tx: anc.x - (anc.x - v0.tx) * (k2 / v0.k),
        ty: anc.y - (anc.y - v0.ty) * (k2 / v0.k),
      };
      const dRot = g.rotation - a0;
      setView(rotateView(marit, rot0, dRot, p, d.w, d.h));
      setRot(rot0 + dRot);
      spre.current.k = k2;
      spre.current.rot = rot0 + dRot;
      spre.current.ancora = anc;
    };
    const end = (e: Event) => {
      e.preventDefault(); v0 = null; gestActiv.current = false;
    };
    el.addEventListener("gesturestart", start);
    el.addEventListener("gesturechange", change);
    el.addEventListener("gestureend", end);
    return () => {
      el.removeEventListener("gesturestart", start);
      el.removeEventListener("gesturechange", change);
      el.removeEventListener("gestureend", end);
    };
  }, []);
}
